import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, SearchX, TriangleAlert, Users, Route, CircleCheck, Star, Settings } from "lucide-react";
import { TspBreadcrumb, TspEmptyState, TspSkeleton, TspStatCard } from "../../components/primitives";
import useTspPermission from "../../hooks/useTspPermission";
import TSP_PERMISSIONS from "../../constants/tspPermissions";
import useTspBusLeadersData from "./useTspBusLeadersData";
import { LEADER_PAGE_SIZE, LEADER_SORTS, LEADER_STATUSES, countByStatus, filterLeaders, stateOptionsOf, summarize } from "./directoryModel";
import LeaderCard from "./components/LeaderCard";

const NO_FILTERS = { search: "", state: "", status: "", sort: "name" };

// Bus Leaders Directory (/tsp/teams): who leads each Travelling Sankirtan
// Party, where they are based, whether they are out on a journey, and how to
// reach them. Team CRUD lives on /tsp/teams/manage.
export default function TspBusLeadersDirectoryPage() {
  const { data, loading, error } = useTspBusLeadersData();
  const { hasPermission } = useTspPermission();
  const canManageTeams = hasPermission(TSP_PERMISSIONS.TEAMS_MANAGE);
  const [filters, setFilters] = useState(NO_FILTERS);
  const [visibleCount, setVisibleCount] = useState(LEADER_PAGE_SIZE);

  const leaders = data?.leaders;
  const filtered = useMemo(() => filterLeaders(leaders || [], filters), [leaders, filters]);
  // Status chip counts follow the search/state filters, not the status itself.
  const counts = useMemo(() => countByStatus(filterLeaders(leaders || [], { ...filters, status: "" })), [leaders, filters]);
  const summary = useMemo(() => summarize(leaders || []), [leaders]);
  const states = useMemo(() => stateOptionsOf(leaders || []), [leaders]);

  const setFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setVisibleCount(LEADER_PAGE_SIZE);
  };
  const clearFilters = () => {
    setFilters(NO_FILTERS);
    setVisibleCount(LEADER_PAGE_SIZE);
  };
  const hasActiveFilters = Boolean(filters.search || filters.state || filters.status);

  const heading = (
    <div className="tsp-page-heading-row">
      <div>
        <h1>Bus Leaders Directory</h1>
        <TspBreadcrumb items={["Dashboard", "Bus Leaders Directory"]} />
      </div>
      {canManageTeams && (
        <Link to="/tsp/teams/manage" className="tsp-btn tsp-btn-outline-primary">
          <Settings size={15} aria-hidden="true" /> Manage Teams
        </Link>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="tsp-page tsp-leaders-page">
        {heading}
        <TspSkeleton count={6} label="Loading bus leaders" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="tsp-page tsp-leaders-page">
        {heading}
        <TspEmptyState
          tone="error"
          icon={TriangleAlert}
          title="Could not load the directory"
          message="Something went wrong while loading bus leaders. Please try again."
          action={
            <button type="button" className="tsp-btn tsp-btn-outline-primary" onClick={() => window.location.reload()}>
              Try again
            </button>
          }
        />
      </div>
    );
  }

  const visible = filtered.slice(0, visibleCount);
  const remaining = filtered.length - visible.length;

  return (
    <div className="tsp-page tsp-leaders-page">
      {heading}

      {leaders.length === 0 ? (
        <TspEmptyState icon={Users} title="No bus leaders yet" message="Team leaders appear here once teams are created and a leader is assigned." />
      ) : (
        <>
          <div className="tsp-stat-cards-row tsp-leaders-stats">
            <TspStatCard icon={Users} value={summary.total} label="Bus Leaders" tone="primary" />
            <TspStatCard icon={Route} value={summary.onJourney} label="On Journey" tone="green" />
            <TspStatCard icon={CircleCheck} value={summary.available} label="Available" tone="blue" />
            <TspStatCard icon={Star} value={summary.avgRating.toFixed(1)} label="Avg Response" tone="amber" />
          </div>

          <div className="tsp-journey-filters" role="search">
            <label className="tsp-journey-search">
              <Search size={16} aria-hidden="true" />
              <input
                type="search"
                placeholder="Search by leader, team, temple or state…"
                aria-label="Search bus leaders"
                value={filters.search}
                onChange={(event) => setFilter("search", event.target.value)}
              />
            </label>
            <select className="tsp-select tsp-filter-select" aria-label="Filter by state" value={filters.state} onChange={(event) => setFilter("state", event.target.value)}>
              <option value="">All states</option>
              {states.map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>
            <select className="tsp-select tsp-filter-select" aria-label="Sort leaders" value={filters.sort} onChange={(event) => setFilter("sort", event.target.value)}>
              {LEADER_SORTS.map((sort) => (
                <option key={sort.value} value={sort.value}>
                  {sort.label}
                </option>
              ))}
            </select>
          </div>

          <div className="tsp-chip-filter-row" role="group" aria-label="Filter by status">
            <button type="button" className="tsp-chip-filter" aria-pressed={filters.status === ""} onClick={() => setFilter("status", "")}>
              All <span>{counts.all}</span>
            </button>
            {LEADER_STATUSES.map((status) => (
              <button
                key={status.key}
                type="button"
                className="tsp-chip-filter"
                aria-pressed={filters.status === status.key}
                onClick={() => setFilter("status", filters.status === status.key ? "" : status.key)}
              >
                {status.label} <span>{counts[status.key]}</span>
              </button>
            ))}
          </div>

          <div className="tsp-results-bar">
            <span className="tsp-muted-text" aria-live="polite">
              Showing {visible.length} of {filtered.length} {filtered.length === 1 ? "leader" : "leaders"}
            </span>
            {hasActiveFilters && (
              <button type="button" className="tsp-filter-clear" onClick={clearFilters}>
                Clear filters
              </button>
            )}
          </div>

          {filtered.length === 0 ? (
            <TspEmptyState
              icon={SearchX}
              title="No bus leaders match your filters"
              message="Try a different search or status, or clear the filters."
              action={
                <button type="button" className="tsp-btn tsp-btn-outline-primary" onClick={clearFilters}>
                  Clear filters
                </button>
              }
            />
          ) : (
            <>
              <div className="tsp-leaders-grid">
                {visible.map((leader) => (
                  <LeaderCard key={leader.id} leader={leader} />
                ))}
              </div>
              {remaining > 0 && (
                <div className="tsp-load-more">
                  <button type="button" className="tsp-btn tsp-btn-outline-primary" onClick={() => setVisibleCount((count) => count + LEADER_PAGE_SIZE)}>
                    Load more ({remaining} remaining)
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
